import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { InfoGraphicAction, InfoGraphicGetter } from '@/store/infoGraphic/infoGraphic.module';
import { InfoGraphicSchema } from '@/store/infoGraphic/infoGraphic.validator';
import { InfoGraphic } from '@/store/infoGraphic/infoGraphic.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'InfoGraphicFormComponent',
  components: {
    BaseModalComponent,
  },
  props: {
    infoGraphicId: {
      type: Number
    },
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const fileDialog = ref();
    const isEditMode = computed(() => !!props.infoGraphicId);
    const infoGraphic = computed<InfoGraphic | undefined>(() => store.getters[InfoGraphicGetter.BY_ID](props.infoGraphicId));
    const title = computed(() => isEditMode.value ? 'แก้ไขประกาศ' : 'เพิ่มประกาศ');
    const infoGraphicForm = ref<InstanceType<typeof BaseModalComponent>>();
    const photoSource = ref('');

    // form
    interface Form {
      id?: number | null;
      title?: string | null;
      pined?: boolean;
      image?: string | null;
    }
    const formData = reactive<Form>({
      id: null,
      title: '',
      pined: false,
      image: '',
    });
    const validator = computed(() => InfoGraphicSchema.validate(formData, { abortEarly: false }).error);
    const errors = reactive({
      title: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'title')),
      description: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'description')),
      image: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'image')),
    });
    const isValid = computed(() => !errors.title && !errors.description && !errors.image);
    const isLoading = computed(() => store.getters[InfoGraphicGetter.STATUS] === 'init' || store.getters[InfoGraphicGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.id = (isEditMode.value) ? props.infoGraphicId : null;
        formData.title = infoGraphic.value?.title;
        formData.pined = (isEditMode.value) ? infoGraphic.value?.pined : false;
        formData.image = infoGraphic.value?.image;

        photoSource.value = (isEditMode.value)
        ? infoGraphic.value?.image as string
        : require('@/assets/images/infoGraphic-nopic.png');

        resolve();
      }).then(() => infoGraphicForm.value?.show());
    };

    // hide modal
    const hide = () => {
      infoGraphicForm.value?.hide();
    };

    // click photo
    const photoClick = () => {
      fileDialog.value?.click();
    };

    // change photo
    const photoChange = (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      const file = files?.item(0);

      if (file) {
        photoSource.value = URL.createObjectURL(file);
        formData.image = file.name;
      }
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.title = '';
        formData.pined = false;
        formData.image = '';
        fileDialog.value.value = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(InfoGraphicAction.UPDATE, {
            id: formData.id,
            title: formData.title,
            pined: formData.pined,
            image: (fileDialog.value?.files?.length !== 0) ? fileDialog.value?.files?.item(0) as File : null,
          }).then(() => hide());
        } else {
          store.dispatch(InfoGraphicAction.CREATE, {
            title: formData.title,
            pined: formData.pined,
            image: fileDialog.value?.files?.item(0) as File,
          }).then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(InfoGraphicAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      infoGraphicForm,
      isEditMode,
      fileDialog,
      photoSource,
      errors,
      submited,
      isLoading,
      show,
      hide,
      photoClick,
      photoChange,
      unsubmit,
      clearForm,
      submitClick,
      deleteClick,
    };
  },
});
